import React from 'react';
import { FiBarChart2, FiArrowUpRight, FiArrowDownRight, FiActivity, FiZap } from 'react-icons/fi';
import { clsx } from 'clsx';
import SafeIcon from '../common/SafeIcon';

const PerformanceComparison = ({ stats, connectionInfo }) => {
  const { ping } = stats;
  
  // Benchmark Data (Industry Averages)
  const BENCHMARKS = {
    '4G_LTE': 75,
    '5G_STD': 35,
    'WIFI_5': 50,
    'SAT_LINK': 600
  };

  const calculateUplift = (benchmark) => {
    if (!ping) return 0;
    return Math.round(((benchmark - ping) / benchmark) * 100);
  };

  const benchmarks = [
    { id: '5G_STD', name: 'Global 5G Avg', value: BENCHMARKS['5G_STD'] },
    { id: '4G_LTE', name: 'Standard 4G', value: BENCHMARKS['4G_LTE'] },
    { id: 'WIFI_5', name: 'Legacy Wi-Fi', value: BENCHMARKS['WIFI_5'] },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted flex items-center gap-2">
          <SafeIcon icon={FiBarChart2} className="text-primary" /> Performance Benchmarks
        </h3>
        <span className="text-[9px] font-bold text-muted uppercase">vs Industry Std</span>
      </div>

      <div className="glass-panel rounded-2xl p-4 border border-white/5 space-y-5">
        {benchmarks.map((bench) => {
          const uplift = calculateUplift(bench.value);
          const isPositive = uplift > 0;

          return (
            <div key={bench.id} className="space-y-2">
              <div className="flex justify-between items-end">
                <div className="space-y-0.5">
                  <div className="text-[10px] font-bold text-muted uppercase tracking-tight">{bench.name}</div>
                  <div className="text-xs font-mono text-white/60">{bench.value}ms</div>
                </div>
                <div className={clsx(
                  "flex items-center gap-1 font-mono font-black text-xs px-2 py-0.5 rounded-lg border",
                  isPositive ? "bg-primary/10 text-primary border-primary/20" : "bg-danger/10 text-danger border-danger/20"
                )}>
                  <SafeIcon icon={isPositive ? FiArrowUpRight : FiArrowDownRight} size={12} />
                  {Math.abs(uplift)}%
                </div>
              </div>
              
              {/* Progress Bar Track */}
              <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden flex gap-0.5">
                {/* Benchmark Representation */}
                <div 
                  className="h-full bg-white/10 transition-all duration-1000" 
                  style={{ width: `${(bench.value / 100) * 100}%` }}
                />
                {/* User Performance Shadow */}
                <div 
                  className={clsx(
                    "h-full transition-all duration-700 delay-300",
                    isPositive ? "bg-primary opacity-80" : "bg-danger opacity-80"
                  )}
                  style={{ width: `${(Math.max(5, Math.abs(uplift)) / 100) * 100}%` }}
                />
              </div>
            </div>
          );
        })}

        <div className="pt-2">
          <div className="bg-primary/5 border border-primary/10 rounded-xl p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/20 shrink-0">
              <SafeIcon icon={FiZap} size={14} className="text-primary animate-pulse" />
            </div>
            <div>
              <div className="text-[9px] font-black text-primary uppercase tracking-widest">Optimization Verdict</div>
              <p className="text-[11px] text-text/80 leading-tight font-medium">
                {ping < 30 
                  ? "Elite 5G signaling detected. Your edge latency is in the top 1% of global mobile nodes."
                  : "Stable connection, but regional routing overhead is adding ~15ms to your 5G baseline."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceComparison;